import React, { Component } from 'react';
import Button from '../components/Button';
import './TechnologyPage.css';

interface StackLayer {
  title: string;
  information: string;
  label: string;
}

interface ResearchPrinciple {
  title: string;
  description: string;
  label: string;
}

interface MotivationSignal {
  title: string;
  description: string;
  label: string;
}

interface UsageScenario {
  title: string;
  description: string;
  label: string;
}

class TechnologyPage extends Component {
  aowlTechPreviewUrl = 'https://www.aowl.app/media/8864da30-fb05-4229-b8c0-d8bbe712ead0/';

  stackLayers: StackLayer[] = [
    {
      title: 'Start with official audio',
      information: 'Teams register the recordings they control: campaign voiceovers, creator clips, executive statements, and licensed media. AOWL creates an identity record before the audio is released.',
      label: 'Register'
    },
    {
      title: 'Embed proof listeners cannot hear',
      information: 'AOWL adds an inaudible identity signal to the audio. The file can still be posted, edited, or compressed like normal media, while the proof travels with it.',
      label: 'Watermark'
    },
    {
      title: 'Check disputed clips against the protected record',
      information: 'When a suspicious clip appears, verification looks for the protected signal and returns evidence a team can use for triage, escalation, or response.',
      label: 'Verify'
    }
  ];

  researchPrinciples: ResearchPrinciple[] = [
    {
      title: 'Survives normal publishing',
      description: 'The proof is designed for the messy path media takes after release: platform compression, reposts, previews, and exports.',
      label: 'Robustness'
    },
    {
      title: 'Keeps audio clean',
      description: 'The protection is inaudible, so audiences hear the original performance while teams keep a verification path in reserve.',
      label: 'Audio quality'
    },
    {
      title: 'Points reviewers to the moment',
      description: 'Timestamped markers make review faster by showing where protected evidence appears in the audio timeline.',
      label: 'Timestamped review'
    },
    {
      title: 'Keeps the sensitive parts private',
      description: 'The public page can show the workflow and review surface without revealing matching logic, keys, or operational details.',
      label: 'Trust boundary'
    }
  ];

  motivationSignals: MotivationSignal[] = [
    {
      title: 'Voice now carries authority',
      description: 'A convincing call, memo, or clip can trigger money movement, data exposure, or reputational damage.',
      label: 'Fraud'
    },
    {
      title: 'Private audio is easy to forward',
      description: 'Drafts, interviews, earnings-call prep, and campaign assets often pass through many hands before release.',
      label: 'Leakage'
    },
    {
      title: 'Incident teams need evidence fast',
      description: 'The question is not only whether something sounds real. Teams need a reviewable signal they can act on.',
      label: 'Response'
    }
  ];

  usageScenarios: UsageScenario[] = [
    {
      title: 'AI voice-clone fraud',
      description: 'Help teams check whether an urgent voice request is tied to protected official audio before money, access, or sensitive data moves.',
      label: '01'
    },
    {
      title: 'Audio canary tokens',
      description: 'Use different inaudible IDs for agencies, partners, or review groups so leaked drafts can point back to a distribution path.',
      label: '02'
    },
    {
      title: 'Controlled pre-release access',
      description: 'Protect unreleased songs, audiobook chapters, campaign voiceovers, interviews, trailers, and internal briefings before sharing.',
      label: '03'
    },
    {
      title: 'Incident investigation',
      description: 'Give PR, legal, and security teams a clearer way to ask where a clip came from, whether it maps to protected media, and what response path fits.',
      label: '04'
    }
  ];

  render() {
    return (
      <div className="technology-page">
        <section className="tech-hero">
          <div className="container tech-hero-grid">
            <p className="eyebrow">Our technology</p>
            <h1 className="page-title">AOWL turns audio into evidence without changing how it sounds.</h1>
            <p className="page-subtitle">
              Register official audio, embed an inaudible identity signal, publish normally, then
              verify suspicious clips against the protected record when provenance matters.
            </p>
          </div>
        </section>

        <section className="tech-motivation-section">
          <div className="container tech-motivation">
            <div className="motivation-lead">
              <p className="eyebrow">Why it matters</p>
              <h2>Voice now needs a security layer, not just a copyright layer.</h2>
              <p>
                Audio watermarking can work like identity and a canary token for media: invisible
                to listeners, reviewable by trusted teams, and useful when a clip becomes a fraud,
                leak, or provenance question.
              </p>
            </div>
            <div className="motivation-video-card" aria-label="Deepfake crisis motivation reel">
              <div className="motivation-video-topline">
                <span>Motivation reel</span>
                <span>29s preview</span>
              </div>
              <div className="motivation-video-frame">
                <video
                  src="/media/crisis-demo-v2-desktop.mp4"
                  poster="/images/crisis-demo-poster.jpg"
                  muted
                  autoPlay
                  loop
                  playsInline
                  controls
                  preload="metadata"
                />
              </div>
            </div>
            <div className="motivation-panel">
              <span className="panel-kicker">Threat model</span>
              {this.motivationSignals.map((signal, index) => (
                <div key={signal.title} className="motivation-row">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <p>{signal.label}</p>
                    <h3>{signal.title}</h3>
                    <small>{signal.description}</small>
                  </div>
                </div>
              ))}
            </div>
            <div className="usage-scenario-section">
              <div className="usage-scenario-heading">
                <p className="eyebrow">Where it applies</p>
                <h3>Audio watermarking gives security teams a signal they can investigate.</h3>
              </div>
              <div className="usage-scenario-grid" aria-label="Audio watermark usage scenarios">
                {this.usageScenarios.map((scenario) => (
                  <div key={scenario.title} className="usage-scenario-card">
                    <span>{scenario.label}</span>
                    <h3>{scenario.title}</h3>
                    <p>{scenario.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="tech-stack-section">
          <div className="container tech-stack-grid">
            <div className="tech-stack-copy">
              <p className="eyebrow">System design</p>
              <h2>A clear workflow with a protected core.</h2>
              <p>
                AOWL keeps the buyer-facing idea simple: protect official audio before release, let
                it move through normal channels, and give reviewers a way to check suspicious clips
                without exposing the underlying detection logic.
              </p>
            </div>
            <div className="tech-stack-content">
              <div className="stack-panel" aria-label="AOWL public technology model">
                <span className="panel-kicker">Public technology model</span>
                {this.stackLayers.map((layer, index) => (
                  <div key={layer.title} className="stack-row">
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <div>
                      <p>{layer.label}</p>
                      <h3>{layer.title}</h3>
                      <small>{layer.information}</small>
                    </div>
                  </div>
                ))}
              </div>
              <div className="research-grid stack-principles">
                {this.researchPrinciples.map((principle) => (
                  <div key={principle.title} className="research-card">
                    <span>{principle.label}</span>
                    <h3>{principle.title}</h3>
                    <p>{principle.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="tech-proof-preview-section">
          <div className="container tech-proof-preview">
            <div className="tech-proof-copy">
              <p className="eyebrow">Proof surface</p>
              <h2>A public review surface for private protection.</h2>
              <p>
                The AOWL preview shows the part a team can understand and share: protected media,
                visible review markers, and a timestamped signal. Keys, matching logic, and
                operational details stay private.
              </p>
              <Button
                variant="primary"
                href={this.aowlTechPreviewUrl}
                external={true}
                className="tech-proof-button"
              >
                <img
                  src="/images/aowl-logo.003.png"
                  alt=""
                  className="button-logo"
                />
                View AOWL media
              </Button>
            </div>
            <div className="tech-proof-card" aria-label="AOWL technology media preview">
              <div className="tech-proof-topline">
                <span>Public AOWL media</span>
                <span>Review preview</span>
              </div>
              <a
                className="tech-proof-stage"
                href={this.aowlTechPreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View the AOWL technology media preview"
              >
                <img
                  src="/images/aowl-tech-preview.jpg"
                  alt="AOWL technology media preview"
                />
                <span className="tech-proof-play">
                  <img src="/images/aowl-logo.003.png" alt="" />
                  Open in AOWL
                </span>
              </a>
              <div className="tech-proof-footer">
                <div>
                  <strong>Watermark review surface</strong>
                  <span>Timestamped public preview</span>
                </div>
                <a href={this.aowlTechPreviewUrl} target="_blank" rel="noopener noreferrer">
                  View media
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="tech-details">
          <div className="container">
            <div className="detail-section">
              <p className="eyebrow">Review questions</p>
              <h2>What teams can ask when audio is disputed.</h2>
              <div className="workflow">
                <div className="workflow-step">
                  <div className="step-number">01</div>
                  <h3>Was this clip protected before release?</h3>
                  <p>Check whether the audio contains the expected identity signal from an official asset.</p>
                </div>
                <div className="workflow-step">
                  <div className="step-number">02</div>
                  <h3>What asset, owner, or campaign does it connect to?</h3>
                  <p>Map the evidence back to the protected record used by the team, creator, or rights holder.</p>
                </div>
                <div className="workflow-step">
                  <div className="step-number">03</div>
                  <h3>What can we do with the result?</h3>
                  <p>Use the verification output to support internal review, public response, platform escalation, or takedown workflows.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="tech-cta-section">
          <div className="container">
            <div className="tech-cta-content">
              <div>
                <p className="eyebrow">AOWL app</p>
                <h2>Start protecting audio now.</h2>
              </div>
              <Button
                variant="primary"
                href="https://aowl.app"
                external={true}
                className="tech-cta-button"
              >
                <img
                  src="/images/aowl-logo.003.png"
                  alt=""
                  className="button-logo"
                />
                Open AOWL
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default TechnologyPage;
